import {useState, useEffect, useCallback, useRef} from 'react'
import {useParams, useNavigate, useSearchParams} from 'react-router-dom'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {Info, Save} from 'lucide-react'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {ReportDefinitionListItemFields, ReportDefinitionDetailFields} from '@/types/reporting'
import {Button} from '@/components/ui/button'
import {Card, CardContent} from '@/components/ui/card'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {toast} from '@/hooks/use-toast'
import {useSqlRunner} from '@/hooks/use-sql-runner'
import type {ReportDefinitionDetail} from './types'
import {SqlEditorPanel} from '@/components/editors/SqlEditorPanel'
import {SqlAssistPanel} from './SqlAssistPanel'
import {ParameterDefinitionEditor, type ParameterDefinitionEditorHandle} from './ParameterDefinitionEditor'
import {ResultTable} from './ResultTable'
import {ScheduleSection} from './ScheduleSection'
import {PageLayout} from '@/components/shared/PageLayout'
import {queryError} from '@/lib/query-utils'
import {ErrorBanner} from '@/components/ui/error-banner'
import {CONCEPT_ICON} from '@/lib/concepts'

/**
 * Unified create + edit page for report definitions.
 * Create mode: /reporting/reports/new?categoryId=...
 * Edit mode: /reporting/reports/:id/edit
 */
export function ReportDefinitionEdit() {
    const services = useServices()
    const i18n = useI18n()
    const {id} = useParams<{id: string}>()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const isCreateMode = !id
    const categoryId = searchParams.get(ReportDefinitionListItemFields.categoryId)

    usePageTitle(isCreateMode ? i18n.reporting.reportDetail.createTitle : i18n.reporting.reportDetail.editReport)

    const paramEditorRef = useRef<ParameterDefinitionEditorHandle>(null)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [sql, setSql] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [scheduleCron, setScheduleCron] = useState<string | null>(null)
    const [recipientIdentityIds, setRecipientIdentityIds] = useState<string[]>([])
    const [recipientEmails, setRecipientEmails] = useState<string[]>([])
    const [notificationTemplateKey, setNotificationTemplateKey] = useState<string | null>(null)
    const [cronFieldError, setCronFieldError] = useState<string | null>(null)
    const {isRunning, result: runResult, error: runError, run: runSql} = useSqlRunner(1000)

    const {data: query, error} = useQuery<ReportDefinitionDetail>({
        queryKey: ['reporting-report-detail', id],
        queryFn: () => services.reporting.reportDetail(id!),
        enabled: !!id,
    })

    useEffect(() => {
        if (query) {
            setName(query.name)
            setDescription(query.description ?? '')
            if (query.queryDefinition?.sql) {
                setSql(query.queryDefinition.sql)
            }
            setScheduleCron(query.scheduleCron ?? null)
            setRecipientIdentityIds(query.recipientIdentityIds ?? [])
            setRecipientEmails(query.recipientEmails ?? [])
            setNotificationTemplateKey(query.notificationTemplateKey ?? null)
        }
    }, [query])


    const handleSave = useCallback(async () => {
        if (isCreateMode) {
            if (!categoryId || !name.trim()) return
            setIsSaving(true)
            try {
                const created = await services.reporting.createReport(categoryId, {
                    name: name.trim(),
                    description: description.trim() || null,
                    queryDefinition: {sql: sql.trim() || ''},
                }) as ReportDefinitionDetail
                queryClient.invalidateQueries({queryKey: ['reporting']})
                toast({description: i18n.reporting.reportSaved})
                navigate(routes.reporting.reports.detail(created.reportDefinitionId), {replace: true})
            } finally {
                setIsSaving(false)
            }
        } else {
            if (!query || !id) return
            setIsSaving(true)
            setCronFieldError(null)
            try {
                await services.reporting.updateReport(id, {
                    name: name.trim(),
                    description: description.trim() || null,
                    version: query.version,
                    queryDefinition: {sql: sql.trim()},
                    scheduleCron: scheduleCron,
                    recipientIdentityIds: recipientIdentityIds,
                    recipientEmails: recipientEmails,
                    notificationTemplateKey: notificationTemplateKey,
                })
                await paramEditorRef.current?.saveAll()
                queryClient.invalidateQueries({queryKey: ['reporting-report-detail', id]})
                toast({description: i18n.reporting.reportSaved})
                navigate(routes.reporting.reports.detail(id))
            } catch (err) {
                // WHY: inline a "field"-tagged 400 (e.g. RP0018_INVALID_CRON_EXPRESSION) on the schedule input rather than as a toast — the cron textbox is where the user fixes it.
                const apiErr = err as {field?: string; status?: number; message?: string}
                if (apiErr?.status === 400 && apiErr.field === ReportDefinitionDetailFields.scheduleCron) {
                    setCronFieldError(apiErr.message ?? i18n.reporting.schedule.invalidCron)
                    return
                }
                throw err
            } finally {
                setIsSaving(false)
            }
        }
    }, [isCreateMode, id, categoryId, query, name, description, sql, scheduleCron,
        recipientIdentityIds, recipientEmails, notificationTemplateKey,
        queryClient, navigate, i18n, services.reporting])

    const handleTestSql = useCallback(() => runSql(sql), [sql, runSql])

    const editError = isCreateMode ? undefined : queryError(false, query, error)

    return (
        <PageLayout
            variant="detail"
            icon={CONCEPT_ICON.report}
            backTo={isCreateMode ? undefined : routes.reporting.reports.detail(id!)}
            loading={!isCreateMode && !query && !error}
            error={editError}
            title={isCreateMode ? i18n.reporting.reportDetail.createTitle : i18n.reporting.reportDetail.editReport}
            data-testid="report-definition-edit-page"
            actions={
                <Button data-testid="btn-save-report" onClick={handleSave}
                        disabled={isSaving || !name.trim()}>
                    {isSaving
                        ? <><ButtonSpinner/>{i18n.common.saving}</>
                        : <><Icon icon={Save} className="mr-2"/>{i18n.reporting.saveReport}</>}
                </Button>
            }
        >

            {/* Name + Description */}
            <Card>
                <CardContent className="pt-6 space-y-4">
                    <DetailGrid columns={2}>
                        <Stack gap="sm">
                            <Label>{i18n.reporting.saveReportDialog.name}</Label>
                            <Input
                                data-testid="input-report-name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                            />
                        </Stack>
                        <Stack gap="sm">
                            <Label>{i18n.common.description}</Label>
                            <Input
                                data-testid="input-report-description"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </Stack>
                    </DetailGrid>
                </CardContent>
            </Card>

            {/* SQL Editor */}
            <Card>
                <CardContent className="pt-6 space-y-4">
                    <SqlEditorPanel
                        sql={sql}
                        onChange={setSql}
                        onRun={handleTestSql}
                        isRunning={isRunning}
                    />
                    <SqlAssistPanel
                        onSuggestion={setSql}
                        errorContext={runError?.message ?? null}
                        currentSql={sql}
                    />
                </CardContent>
            </Card>

            {/* Test Results */}
            {runError && <ErrorBanner error={runError}/>}
            {runResult && (
                <Card>
                    <CardContent className="pt-6">
                        <ResultTable result={runResult} queryId="" isRunning={isRunning}/>
                    </CardContent>
                </Card>
            )}

            {/* Parameter Definitions — only in edit mode (need report ID for API) */}
            {!isCreateMode && id && (
                <Card>
                    <CardContent className="pt-6">
                        <ParameterDefinitionEditor ref={paramEditorRef} reportDefinitionId={id}/>
                    </CardContent>
                </Card>
            )}

            {/* Schedule section — only in edit mode (needs persisted id) */}
            {!isCreateMode && id && (
                <ScheduleSection
                    scheduleCron={scheduleCron}
                    recipientIdentityIds={recipientIdentityIds}
                    recipientEmails={recipientEmails}
                    notificationTemplateKey={notificationTemplateKey}
                    cronFieldError={cronFieldError}
                    onChange={(next) => {
                        setScheduleCron(next.scheduleCron)
                        setRecipientIdentityIds(next.recipientIdentityIds)
                        setRecipientEmails(next.recipientEmails)
                        setNotificationTemplateKey(next.notificationTemplateKey)
                        if (cronFieldError) setCronFieldError(null)
                    }}
                />
            )}

            {isCreateMode && (
                <InfoBanner variant="info" icon={Info}>
                    {i18n.reporting.reportDetail.createParameterHintPrefix}{' '}
                    <Button
                        variant="link"
                        size="sm"
                        data-testid="hint-save-link"
                        className="p-0 h-auto"
                        onClick={handleSave}
                        disabled={isSaving || !name.trim()}
                    >
                        {i18n.reporting.reportDetail.createParameterHintLink}
                    </Button>{' '}
                    {i18n.reporting.reportDetail.createParameterHintSuffix}
                </InfoBanner>
            )}

        </PageLayout>
    )
}
